import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Clock, Coins, Gauge, Percent } from 'lucide-react';
import { api } from '../../lib/api';
import { formatMoney, formatHours } from '../../lib/utils';
import {
  KpiCard, KpiRow, ChartCard, TableCard, TabLoading, TabError, CHART, ANIM,
  TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE, TOOLTIP_CURSOR, LINE_CURSOR, GRID_STROKE, AXIS_PROPS, LEGEND_PROPS,
  euroTick, euroFull, paddedDomain,
} from './StatsShared';

export function TrendsTab({ year }: { year: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stats-trends', year],
    queryFn: () => api.stats.trends([year - 2, year - 1, year]),
  });

  if (isLoading) return <TabLoading />;
  if (error || !data) return <TabError error={error} />;

  const yearBar = data.by_year.map((y) => ({
    year: String(y.year),
    Bruto: Math.round(y.bruto * 100) / 100,
    Netto: Math.round(y.netto * 100) / 100,
    Reiskosten: Math.round(y.reiskosten * 100) / 100,
    Uren: Math.round(y.uren),
    ORT_pct: Math.round(y.ort_pct * 10) / 10,
  }));

  // Per-periode matrix: X = P1..P13, lijn per jaar
  const periodMap: Record<number, Record<string, number | null>> = {};
  for (let p = 1; p <= 13; p++) periodMap[p] = { period: p };
  for (const row of data.period_comparison) {
    periodMap[row.period_number] = periodMap[row.period_number] ?? { period: row.period_number };
    periodMap[row.period_number][String(row.year)] = row.bruto;
  }
  const perPeriodYoY = Object.values(periodMap).sort((a: any, b: any) => (a.period as number) - (b.period as number));

  const years = data.years.slice().sort((a, b) => a - b);
  // Oudste jaar het zwakst, huidig jaar het helderst.
  const colors = ['#454B54', '#7B828C', CHART.main];

  return (
    <div className="space-y-4 sm:space-y-6">
      <KpiRow>
        <KpiCard icon={Coins} label="Bruto" kpi={data.kpis.bruto_yoy} fmt="money" prevYear={year - 1} />
        <KpiCard icon={Clock} label="Uren" kpi={data.kpis.uren_yoy} fmt="hours" prevYear={year - 1} />
        <KpiCard icon={Percent} label="Aandeel toeslagen" kpi={data.kpis.ort_pct_yoy} fmt="pct" prevYear={year - 1} />
        <KpiCard icon={Gauge} label="Gemiddeld uurloon" kpi={data.kpis.gem_uurloon_yoy} fmt="eur_per_hour" prevYear={year - 1} />
      </KpiRow>

      <ChartCard title="Per jaar" subtitle="Bruto, netto en onbelaste reiskosten">
        <ResponsiveContainer>
          <BarChart data={yearBar} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="year" {...AXIS_PROPS} />
            <YAxis {...AXIS_PROPS} tickFormatter={euroTick} width={52} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={TOOLTIP_CURSOR} formatter={(v: number) => formatMoney(v)} />
            <Legend {...LEGEND_PROPS} />
            <Bar dataKey="Bruto" fill={CHART.main} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="Netto" fill={CHART.money} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            <Bar dataKey="Reiskosten" fill={CHART.muted} radius={[4, 4, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <TableCard title="Per jaar">
        <table className="table whitespace-nowrap">
          <thead>
            <tr>
              <th>Jaar</th>
              <th className="r">Uren</th>
              <th className="r">Bruto</th>
              <th className="r">Netto</th>
              <th className="r">Reiskosten</th>
              <th className="r">Toeslagen</th>
            </tr>
          </thead>
          <tbody>
            {data.by_year.map((y) => (
              <tr key={y.year}>
                <td className={y.year === year ? 'font-medium text-slate-100' : 'text-slate-300'}>{y.year}</td>
                <td className="r">{formatHours(y.uren)}</td>
                <td className="r">{formatMoney(y.bruto)}</td>
                <td className="r text-money">{formatMoney(y.netto)}</td>
                <td className="r text-slate-400">{formatMoney(y.reiskosten)}</td>
                <td className="r text-slate-400">{y.ort_pct.toFixed(1).replace('.', ',')}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>

      <ChartCard title="Aandeel toeslagen in je bruto" subtitle="Avond-, nacht-, weekend- en feestdagtoeslag als percentage">
        <ResponsiveContainer>
          <LineChart data={yearBar} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="year" {...AXIS_PROPS} padding={{ left: 24, right: 24 }} />
            <YAxis {...AXIS_PROPS} unit="%" domain={paddedDomain(2)} width={44} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={LINE_CURSOR} formatter={(v: unknown) => [`${Number(v).toFixed(1).replace('.', ',')}%`, 'Toeslagen']} />
            <Line type="monotone" dataKey="ORT_pct" stroke={CHART.main} strokeWidth={2} dot={{ r: 4, fill: CHART.main }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Bruto per periode, per jaar" subtitle="Lijnen stoppen waar een jaar nog niet verder is" height={320}>
        <ResponsiveContainer>
          <LineChart data={perPeriodYoY} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke={GRID_STROKE} vertical={false} />
            <XAxis dataKey="period" {...AXIS_PROPS} tickFormatter={(p) => `P${p}`} interval="preserveStartEnd" />
            <YAxis {...AXIS_PROPS} tickFormatter={euroTick} domain={paddedDomain(500)} width={52} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={LINE_CURSOR} labelFormatter={(p) => `Periode ${p}`} formatter={(v: unknown) => euroFull(v)} />
            <Legend {...LEGEND_PROPS} />
            {years.map((y, i) => {
              const isLast = i === years.length - 1;
              return (
                <Line
                  key={y}
                  type="monotone"
                  dataKey={String(y)}
                  name={String(y)}
                  stroke={colors[colors.length - years.length + i] ?? CHART.muted}
                  strokeWidth={isLast ? 2.5 : 1.5}
                  strokeDasharray={isLast ? '' : '4 3'}
                  connectNulls
                  dot={isLast ? { r: 2.5, fill: CHART.main } : false} isAnimationActive={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
